import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {SegmentedControl} from '@/components/segmented-control'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Checkbox} from '@/components/ui/checkbox'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {useTags} from '@/hooks/empresa/organizacion/use-tags'
import {useCrearObra, useEditarObra, type Obra, type ObraEstado} from '@/hooks/empresa/obras/use-obras'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const ESTADOS: ObraEstado[] = ['activo', 'terminado']

/** Las sedes son casillas de las ya declaradas: Kairos no crea sedes desde una obra. */
export function ObraFormDialog({
  businessId,
  obra,
  open,
  onOpenChange,
  onSaved,
}: {
  businessId: string
  obra: Obra | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (obra: Obra) => void
}) {
  const {t} = useTranslation()
  const crear = useCrearObra(businessId)
  const editar = useEditarObra(businessId, obra?.id ?? '')
  const sedes = useTags(businessId, 'sedes', open)
  const [name, setName] = useState('')
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [estado, setEstado] = useState<ObraEstado>('activo')
  const [sedeIds, setSedeIds] = useState<string[]>([])
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const pending = crear.isPending || editar.isPending

  // Radix solo reporta los cierres del usuario: una apertura programática se siembra en un
  // efecto de `open`, nunca en `onOpenChange`, o «Editar» abre en blanco y borra las sedes.
  useEffect(() => {
    if (!open) return
    setName(obra?.name ?? '')
    setFechaInicio(obra?.fecha_inicio ?? '')
    setFechaFin(obra?.fecha_fin ?? '')
    setEstado(obra?.estado ?? 'activo')
    setSedeIds(obra?.sede_ids ?? [])
    setNameError(undefined)
    setError(null)
  }, [open, obra])

  function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    const problema = validateRequired(name) ?? validateRequired(fechaInicio)
    setNameError(validateRequired(name) ?? undefined)
    if (problema) return
    const payload = {
      name: name.trim(),
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin || null,
      sede_ids: sedeIds,
    }
    const options = {
      onSuccess: (guardada: Obra) => {
        onOpenChange(false)
        onSaved?.(guardada)
      },
      onError: (err: unknown) => setError(translateApiError(err)),
    }
    if (obra) editar.mutate({...payload, estado}, options)
    else crear.mutate(payload, options)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="obra-form-dialog">
        <DialogHeader>
          <DialogTitle>{obra ? t('obra.form.editTitle') : t('obra.form.newTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="obra_name">{t('obra.form.name')}</Label>
            <Input
              maxLength={tope('NOM')}
              id="obra_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameError(validateRequired(name) ?? undefined)}
              data-testid="obra-name"
            />
            <FieldError error={nameError} />
          </div>
          <DateField
            id="obra_fecha_inicio"
            label={t('obra.form.fechaInicio')}
            value={fechaInicio}
            onChange={setFechaInicio}
          />
          <DateField
            id="obra_fecha_fin"
            label={t('obra.form.fechaFin')}
            value={fechaFin}
            min={fechaInicio || undefined}
            hint={t('obra.form.fechaFinHint')}
            onChange={setFechaFin}
          />
          {/* Solo al editar: una obra nace activa. */}
          {obra && (
            <div className="space-y-2">
              <Label>{t('obra.form.estado')}</Label>
              <SegmentedControl
                value={estado}
                options={ESTADOS.map((clave) => ({key: clave, label: t(`obra.estado.${clave}`)}))}
                testId="obra-estado"
                onChange={setEstado}
              />
              {estado === 'terminado' && (
                <p className="text-xs text-muted-foreground" data-testid="obra-terminar-hint">
                  {t('obra.form.terminarHint')}
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>{t('obra.form.sedes')}</Label>
            <p className="text-xs text-muted-foreground">{t('obra.form.sedesHint')}</p>
            {sedes.data && sedes.data.items.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="obra-sedes-vacio">
                {t('obra.form.sedesEmpty')}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {(sedes.data?.items ?? []).map((sede) => (
                  <li key={sede.id} className="flex items-center gap-2">
                    {/* `aria-label` y no `<Label htmlFor>`: el checkbox de la casa es un
                        `<button role="checkbox">` y un `for` no lo activa. */}
                    <Checkbox
                      aria-label={sede.name}
                      checked={sedeIds.includes(sede.id)}
                      onCheckedChange={(marcada) =>
                        setSedeIds((previas) =>
                          marcada ? [...previas, sede.id] : previas.filter((otra) => otra !== sede.id)
                        )
                      }
                      data-testid={`obra-sede-${sede.id}`}
                    />
                    <span className="text-sm text-foreground">{sede.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <FormDialogFooter
            error={error}
            pending={pending}
            submitLabel={t('obra.form.save')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
